import { Card, DatePicker, Empty, Radio, RadioGroup, Skeleton, TabPane, Tabs, Typography } from "@douyinfe/semi-ui";
import { VChart } from "@visactor/react-vchart";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from "recharts";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { AssetsDto, DashboardFilters, HomeDashboardApi, SliceItem } from "../api/homeDashboard";
import { useDashboardModule } from "../hooks/useDashboardModule";
import { chartBoxStyle, chartTitleStyle } from "../styles";
import { DrawerMode } from "./DetailDrawer";

interface Props {
  filters: DashboardFilters;
  isAdmin: boolean;
  currentTeamId?: number;
  openDrawer: (title: string, params: Record<string, any>, mode?: DrawerMode) => void;
}

type QuickKey = "week" | "month" | "half" | "year";

const DEFAULT_RANGE: [Date, Date] = [dayjs().startOf("month").toDate(), dayjs().toDate()];

function rangeForKey(key: QuickKey): [Date, Date] {
  const today = dayjs();
  switch (key) {
    case "week": return [today.startOf("week").toDate(), today.toDate()];
    case "month": return [today.startOf("month").toDate(), today.toDate()];
    case "half": return [today.subtract(6, "month").toDate(), today.toDate()];
    case "year": return [today.subtract(1, "year").toDate(), today.toDate()];
  }
}

function detectQuickKey(range: [Date, Date]): QuickKey | "" {
  const keys: QuickKey[] = ["week", "month", "half", "year"];
  for (const k of keys) {
    const [s, e] = rangeForKey(k);
    if (dayjs(s).isSame(dayjs(range[0]), "day") && dayjs(e).isSame(dayjs(range[1]), "day")) {
      return k;
    }
  }
  return "";
}

export default function CustomerAssetsCard({ filters, openDrawer }: Props) {
  const [range, setRange] = useState<[Date, Date]>(DEFAULT_RANGE);
  const [tab, setTab] = useState<"byType" | "byStatus" | "byStar">("byType");

  const localFilters: DashboardFilters = {
    ...filters,
    startDate: dayjs(range[0]).format("YYYY-MM-DD"),
    endDate: dayjs(range[1]).format("YYYY-MM-DD"),
  };

  const enabled = !!localFilters.startDate && !!localFilters.endDate;
  const { data, loading } = useDashboardModule<AssetsDto>(
    () => HomeDashboardApi.getAssets(localFilters),
    [localFilters.startDate, localFilters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    enabled
  );

  const pieData = useMemo(() => {
    if (!data) return [] as { name: string; value: number; key: string; percent: number }[];
    const src: SliceItem[] = tab === "byType" ? data.byType : tab === "byStatus" ? data.byStatus : data.byStar;
    return src.map((s: SliceItem) => ({ name: s.label, value: s.count, key: s.key, percent: s.percent }));
  }, [data, tab]);

  const radarDetail = useMemo(() => buildRadarDetail(data), [data]);
  const radarData = useMemo(() => radarDetail.map(d => ({ dim: d.dim, score: d.score })), [radarDetail]);

  const quickValue = detectQuickKey(range);

  return (
    <Card>
      <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
        
        <DatePicker type="dateRange" style={{width:250}} value={range} onChange={(v: any) => v && v.length === 2 && setRange([v[0], v[1]])} />
        <RadioGroup value={quickValue} onChange={(e) => {
          const key = e.target.value as QuickKey;
          if (key) setRange(rangeForKey(key));
        }}>
          <Radio value="week">本周</Radio>
          <Radio value="month">本月</Radio>
          <Radio value="half">近半年</Radio>
          <Radio value="year">近1年</Radio>
        </RadioGroup>
      </div>

      {loading && <Skeleton.Title />}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={chartBoxStyle}>
          <div style={{ display: "flex", gap: 12, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
          <Typography.Text style={chartTitleStyle}>多维度占比</Typography.Text>
          <Tabs type="line" onChange={(k) => setTab(k as any)}>
            <TabPane tab="按类型" itemKey="byType" />
            <TabPane tab="按状态" itemKey="byStatus" />
            <TabPane tab="按星级" itemKey="byStar" />
          </Tabs>
          </div>
          {pieData.length > 0 ? (
            <VChart spec={{
              type: "pie", data: [{ values: pieData }],
              valueField: "value", categoryField: "name",
              label: { visible: true, text: "{name}\n{value}", position: "outside" },
              pie: { center: ["50%", "50%"], radius: ["35%", "65%"] },
              tooltip: { mark: { title: { value: (d: any) => d?.["name"] }, content: [{ key: (d: any) => d?.["name"], value: (d: any) => `${d?.["value"]} (${(d?.["percent"] ?? 0).toFixed(1)}%)` }] } },
              legends: [{ visible: true, position: "right", orient: "vertical" }],
            } as any} onClick={(e: any) => {
              const name = e?.datum?.["name"];
              const item = pieData.find(p => p.name === name);
              if (item) openDrawer(`客户结构 - ${item.name}`, { [tabKeyToParam(tab)]: item.key, startDate: localFilters.startDate, endDate: localFilters.endDate, teamId: filters.teamId, saleIds: filters.saleIds });
            }} style={{ height: 300 }} />
          ) : !loading ? <Empty title="暂无数据" /> : null}
        </div>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>资质雷达（优质档占比 / 客户数）</Typography.Text>
          {loading && <Skeleton.Title />}
          {radarData.length > 0 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch" }}>
              <div style={{ flex: "1 1 260px", minWidth: 300 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData} outerRadius="72%" style={{width:500}}>
                    <PolarGrid />
                   
                    <PolarAngleAxis
                      dataKey="dim"
                      tick={{ fontSize: 11, fontWeight: 600, fill: "var(--semi-color-text-1)" }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fontSize: 10, fill: "var(--semi-color-text-2)" }}
                      tickFormatter={(v: any) => `${v}%`}
                      stroke="var(--semi-color-border)"
                    />
                    <Radar
                      name="优质率"
                      dataKey="score"
                      stroke="#0093FF"
                      strokeWidth={2}
                      fill="#0093FF"
                      fillOpacity={0.25}
                      dot={{ r: 4, fill: "#0093FF", stroke: "#fff", strokeWidth: 1.5 }}
                      label={{ fontSize: 11, fontWeight: 600, fill: "#0093FF" }}
                    />
                    <Tooltip
                      formatter={(v: any) => [`${v}%`, "优质率"]}
                      contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: "0 0 200px", minWidth: 180, alignSelf: "center" }}>
                <RadarDetailList detail={radarDetail} />
              </div>
            </div>
          ) : !loading ? <Empty title="暂无资质数据" /> : null}
        </div>
      </div>
    </Card>
  );
}

function tabKeyToParam(tab: string): string {
  return tab === "byType" ? "leadType" : tab === "byStatus" ? "status" : "star";
}

const GOOD_WORDS: Record<string, string[]> = {
  job: [
    "公务员", "企业主",
    "事业单位", "央企", "国企",
    "外企", "上市", "大型民企",
  ],
  income: [
    "1~2万", "2万以上",
    "2~5万", "5万以上",
  ],
  house: [
    "有房无按揭",
    "全款住宅", "全款公寓", "全款商铺",
    "一抵", "二抵",
    "2套", "3套",
  ],
  vehicle: [
    "有车无按揭",
    "全款燃油", "全款新能源",
  ],
  socialSecurity: [
    "有社保",
    "单位缴纳", "个人缴纳",
  ],
  providentFund: [
    "有公积金",
    "月缴500~1500", "月缴1500以上",
  ],
  credit: [
    "良好",
    "正常", "无逾期",
  ],
  loanType: [
    "信用贷", "公积金贷",
    "保单贷", "月供贷",
  ],
};

const DIM_MAP: Record<string, keyof AssetsDto["qualifications"]> = {
  "工作": "job", "收入": "income", "房产": "house", "车产": "vehicle",
  "社保": "socialSecurity", "公积金": "providentFund", "信用": "credit", "负债": "loanType",
};

function buildRadarDetail(data?: AssetsDto): { dim: string; total: number; good: number; score: number }[] {
  if (!data) return [];
  return Object.entries(DIM_MAP).map(([label, key]) => {
    const items = data.qualifications[key] || [];
    const total = items.reduce((s, i) => s + i.count, 0);
    const words = GOOD_WORDS[key] || [];
    const good = items
      .filter(i => words.some(w => i.label.includes(w)))
      .reduce((s, i) => s + i.count, 0);
    return { dim: label, total, good, score: total > 0 ? Math.round(good * 100 / total) : 0 };
  });
}

function scoreColor(score: number): string {
  if (score >= 70) return "#00B42A";
  if (score >= 40) return "#FF7D00";
  return "#F53F3F";
}

function RadarDetailList({ detail }: { detail: { dim: string; total: number; good: number; score: number }[] }) {
  return (
    <div>
      <div style={{ display: "flex", fontSize: 11, color: "var(--semi-color-text-2)", paddingBottom: 4, borderBottom: "1px solid var(--semi-color-border)" }}>
        <div style={{ flex: 1 }}>维度</div>
        <div style={{ width: 64, textAlign: "right" }}>优质/总数</div>
        <div style={{ width: 44, textAlign: "right" }}>占比</div>
      </div>
      {detail.map(d => (
        <div key={d.dim} style={{
          display: "flex", alignItems: "center", padding: "5px 0",
          borderBottom: "1px dashed var(--semi-color-border-light, var(--semi-color-border))",
          fontSize: 12,
        }}>
          <div style={{ flex: 1, color: "var(--semi-color-text-1)" }}>{d.dim}</div>
          <div style={{ width: 64, textAlign: "right", color: "var(--semi-color-text-2)", fontVariantNumeric: "tabular-nums" }}>
            <span style={{ color: scoreColor(d.score), fontWeight: 600 }}>{d.good}</span>
            <span style={{ color: "var(--semi-color-text-3)" }}> / {d.total}</span>
          </div>
          <div style={{ width: 44, textAlign: "right", color: scoreColor(d.score), fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {d.score}%
          </div>
        </div>
      ))}
    </div>
  );
}
